import React from 'react';
import { orderBy } from 'lodash';

import CardDialog from '~/components/CardDialog';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import Text from '~/components/core/TextComponents/Text';
import type { DocumentType } from '~/components/types/document-type';

/** Shown as the selection for a configuration that has never nominated a type. */
export const NO_FALLBACK_SELECTED_LABEL = 'Other';

export const getFallbackDocumentTypeLabel = (
  fallbackDocumentTypeKey: string | null | undefined,
  documentTypesDict: Record<string, DocumentType>
): string => {
  if (!fallbackDocumentTypeKey) {
    return NO_FALLBACK_SELECTED_LABEL;
  }
  // A type selected earlier and disabled since is no longer in the enabled catalog. Naming the
  // raw key keeps the cell honest instead of silently reading as "nothing selected", which is a
  // different configuration and a different stored document type.
  return documentTypesDict[fallbackDocumentTypeKey]?.display_name || fallbackDocumentTypeKey;
};

interface FallbackDocumentTypeDialogProps {
  configurationName: string;
  fallbackDocumentTypeKey: string | null | undefined;
  documentTypesDict: Record<string, DocumentType>;
  isSubmitting: boolean;
  onSave: (fallbackDocumentTypeKey: string | null) => Promise<void> | void;
  onClose: () => void;
}

const FallbackDocumentTypeDialog: React.FC<FallbackDocumentTypeDialogProps> = ({
  configurationName,
  fallbackDocumentTypeKey,
  documentTypesDict,
  isSubmitting,
  onSave,
  onClose,
}) => {
  const [selectedKey, setSelectedKey] = React.useState<string>(fallbackDocumentTypeKey || '');

  const documentTypeOptions = orderBy(
    Object.values(documentTypesDict),
    (documentType) => documentType.display_name.toLowerCase(),
    'asc'
  );

  return (
    <CardDialog
      title="Classification Fallback Document Type"
      isDialog
      open={true}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={() => onSave(selectedKey || null)}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-16">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          The document type a document lands on when Clive cannot classify it, for <strong>{configurationName}</strong>.
          Clear the selection to keep the catch-all <strong>Other</strong>.
        </Text>
        <SingleSelectField<string>
          id="fallback_document_type_key"
          label="Fallback Document Type"
          value={selectedKey}
          fullWidthField
          clearable
          clearSelection={() => setSelectedKey('')}
          disabled={isSubmitting}
          // Selecting nothing is a real state — the catch-all — so an organization that happens to
          // have one enabled document type must not have it chosen on its behalf.
          autoSelectSingleOption={false}
          onChange={(value) => setSelectedKey(typeof value === 'string' ? value : '')}
        >
          {documentTypeOptions.map((documentType) => (
            <MenuItem key={documentType.key} value={documentType.key}>
              {documentType.display_name}
            </MenuItem>
          ))}
        </SingleSelectField>
      </div>
    </CardDialog>
  );
};

export default FallbackDocumentTypeDialog;
